import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { DemoChip } from '@frontend/analytics-shared';
import { CallIntelTooltip, generateTrackingId, useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { Chart, XAxisLabelValueTick } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { trackingIds } from '../../../tracking-ids';
import { CompareLocationsButton } from '../../compare-locations-button';
import { CustomTooltipTitle } from '../custom-tooltip-title';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelShallowStore, type CallIntelSubViewType } from '../hooks/use-call-intel-store';

type OpportunitiesByTypeChartProps = {
  focusLabel: string;
  infoTipId?: CallIntelTypes.InfoTipId;
  isLoading?: boolean;
  onCompareLocationsClick?: (() => void) | null;
  subViewType: Exclude<CallIntelSubViewType, 'follow-up'>;
  title: string;
  types?: Record<string, number>;
};

export const OpportunitiesByTypeChart = ({
  focusLabel,
  infoTipId,
  isLoading,
  onCompareLocationsClick,
  subViewType,
  types = {},
  title,
}: OpportunitiesByTypeChartProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('analytics');
  const { showDemoChipAndBanner } = useCallIntelDemoFlags();
  const { chartOutlineColors, chartLabelColors, dataColors, dataLabels } = useCallIntelDefaultsShallowStore(
    'chartOutlineColors',
    'chartLabelColors',
    'dataColors',
    'dataLabels'
  );
  const { setSubView } = useCallIntelShallowStore('setSubView');
  const { filterHintText } = useActiveFilters();

  const dataKey = subViewType === 'appointment-type' ? 'appointmentTypes' : 'categories';

  const colors = dataColors[dataKey] || {};
  const labelColors = chartLabelColors[dataKey] || {};
  const strokeColors = chartOutlineColors[dataKey] || {};

  const labels = dataLabels[dataKey] || {};

  const chartDataGroups = Object.entries(types).map(([name, value]) => ({
    name,
    values: {
      [name]: value,
    },
  }));

  return (
    <Chart
      colors={colors}
      isLoading={isLoading}
      labels={{ ...labels, type: focusLabel }}
      strokeColors={strokeColors}
      style={{ marginBottom: 0 }}
      css={styles.chartStyles}
    >
      <Chart.HeaderV2
        title={
          <Chart.HeaderV2.Title
            leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
            infoTip={infoTipId && <CallIntelTooltip tip={infoTipId} />}
          >
            {title}
          </Chart.HeaderV2.Title>
        }
        action={<Chart.HeaderV2.FilterText filterHintText={filterHintText} />}
        subTitle={
          <>
            <Text color='subdued' size='medium'>
              {t('Click each bar to learn more.')}
            </Text>
            {onCompareLocationsClick && (
              <CompareLocationsButton
                onClick={onCompareLocationsClick}
                trackingId={
                  dataKey === 'appointmentTypes'
                    ? trackingIds.callIntel.compareLocationsAppointments
                    : trackingIds.callIntel.compareLocationsCategories
                }
              />
            )}
          </>
        }
      />
      <Chart.BarChart
        appearance={{
          customTooltipTitle: ({ groupName }) => {
            return <CustomTooltipTitle label={labels?.[groupName] || groupName} />;
          },
          customXAxisTick: ({ labels, groupName, ...rest }) => {
            const key = groupName as keyof typeof colors;
            return (
              <XAxisLabelValueTick
                {...rest}
                color={labelColors[key]}
                label={labels?.[groupName] || groupName}
                value={formatters.value(types[key])}
              />
            );
          },
          customTooltipData: () => {
            return Object.entries(types ?? {}).map(([key, value]) => ({
              id: key,
              label: dataLabels[dataKey]?.[key] || key,
              color: labelColors[key] || theme.colors.neutral50,
              formattedValue: formatters.value(value),
            }));
          },
          margin: { bottom: 20 },
          showXAxis: true,
        }}
        data={{
          groups: chartDataGroups,
        }}
        formatValue={formatters.value}
        onClick={({ group }) => {
          setSubView({
            id: group,
            type: subViewType,
          });
          navigate({
            search: {
              type: subViewType,
            },
            to: '/analytics/call-intelligence/$id',
            params: {
              id: group.toLowerCase(),
            },
          });
        }}
        baseTrackingId={generateTrackingId({ component: `calls-by-${subViewType}-bar-chart` })}
      />
    </Chart>
  );
};

const styles = {
  chartStyles: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};

    .chart-content {
      border: none;
      margin-bottom: 0;
      padding: ${theme.spacing(3)};
    }
  `,
};
